import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { postRequest } from "../../Requests/postRequests";
import { fetchRequests } from "../../Requests/getRequests";
import { show } from '../../slices/toastSlice';
import Logo from "../../assets/images/logo.png";

// Landing page for the staff invite email: the invited person sets their
// password here, which activates their account.
function AcceptInvite() {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token") || "";

    const [invite, setInvite] = useState(null);
    const [linkError, setLinkError] = useState("");
    const [loading, setLoading] = useState(true);
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [process, setProcess] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const check = async () => {
            const res = await fetchRequests(`/auth/staff/invite`, "GET", {}, { token }, {});
            if (cancelled) return;
            if (!res || res?.err) setLinkError(res?.err || "This invite link is invalid or has expired.");
            else setInvite(res);
            setLoading(false);
        };
        check();
        return () => { cancelled = true; };
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (password.length < 8) {
            dispatch(show({ type: "error", state: true, message: "Password must be at least 8 characters" }));
            return;
        }
        if (password !== confirm) {
            dispatch(show({ type: "error", state: true, message: "Passwords do not match" }));
            return;
        }

        setProcess(true);
        const res = await postRequest(`/auth/staff/accept-invite`, { token, password });
        setProcess(false);

        if (!res || res?.err) {
            dispatch(show({ type: "error", state: true, message: res?.err || "Something went wrong. Please try again." }));
            return;
        }

        dispatch(show({ type: "success", state: true, message: res.message }));
        navigate("/login");
    };

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-gray-950 flex items-center justify-center p-4">
            <div className="w-full max-w-sm">

                <div className="flex justify-center mb-8">
                    <img src={Logo} alt="CedisPay" className="h-10 w-auto object-contain" />
                </div>

                <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-8">
                    {loading ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400 text-center">Checking your invite…</p>
                    ) : linkError ? (
                        <>
                            <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">Invite link not valid</h1>
                            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{linkError}</p>
                            <Link to="/login" className="mt-6 inline-block text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline">
                                Go to login
                            </Link>
                        </>
                    ) : (
                        <>
                            <div className="mb-7">
                                <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
                                    Set your password
                                </h1>
                                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                    Welcome, {invite?.otherNames}. Choose a password for{" "}
                                    <span className="font-medium text-gray-700 dark:text-gray-300">{invite?.email}</span>
                                </p>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-5">
                                <div>
                                    <label htmlFor="password" className="form-label">Password</label>
                                    <input
                                        type="password"
                                        id="password"
                                        name="password"
                                        autoComplete="new-password"
                                        required
                                        minLength={8}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="form-input"
                                        placeholder="At least 8 characters"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="confirm" className="form-label">Confirm password</label>
                                    <input
                                        type="password"
                                        id="confirm"
                                        name="confirm"
                                        autoComplete="new-password"
                                        required
                                        value={confirm}
                                        onChange={(e) => setConfirm(e.target.value)}
                                        className="form-input"
                                        placeholder="••••••••"
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={process}
                                    className="w-full h-11 flex items-center justify-center rounded-xl bg-light-blue hover:bg-dark-blue text-white text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed mt-2"
                                >
                                    {process ? "Saving…" : "Set password"}
                                </button>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default AcceptInvite;
